import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useGlobalFilter, useTable, forwardRef, useRowSelect, useSortBy, useFilters } from 'react-table';
import { Button, Col, Input, InputGroup, Row } from 'reactstrap';
import { CustomButton } from '../button';
import { Checkbox, Pagination, Tooltip } from 'antd';
import { CaretDownFilled, CaretUpFilled, ClearOutlined, FileTextOutlined, FilterFilled, LayoutOutlined, LeftOutlined, MergeCellsOutlined, PrinterOutlined, ReloadOutlined, RightOutlined, SaveOutlined } from '@ant-design/icons';
import Sort from './sort';
import { ColumnFilter } from './columnfilter';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { convertToIST } from '../../utils/ConvertUTCToLocale';
import { colorCodes, ComputeOperatorsData } from '../../components/common/utils/ConfigOperators';
import { ColumnFilterUnique } from './columnfilterUnqiue';
import Switch from "react-switch";
import moment from 'moment';


interface Props {
    indeterminate?: boolean;
}

/**
 * function to add checkbopx and use to select each row
 * create refs
 * @param refs 
 * @returns 
 */
const useCombinedRefs = (...refs): React.MutableRefObject<any> => {
    const targetRef = React.useRef();

    React.useEffect(() => {
        refs.forEach(ref => {
            if (!ref) return;

            if (typeof ref === 'function') {
                ref(targetRef.current);
            } else {
                ref.current = targetRef.current;
            }
        });
    }, [refs]);

    return targetRef;
};

const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Create checkbox function
 */
const IndeterminateCheckbox = React.forwardRef<HTMLInputElement, Props>(
    ({ indeterminate, ...rest }, ref: React.Ref<HTMLInputElement>) => {
        const defaultRef = React.useRef(null);
        const combinedRef = useCombinedRefs(ref, defaultRef);

        useEffect(() => {
            if (combinedRef?.current) {
                combinedRef.current.indeterminate = indeterminate ?? false;
            }
        }, [combinedRef, indeterminate]);

        return (
            <React.Fragment>
                <input type="checkbox" ref={combinedRef} {...rest} />
            </React.Fragment>
        );
    }
);

function Table(props: any) {

    const { searchHelpText = "Search", columns, data, defaultdata, customClassName, isSort, isSelect, isSelectForm, isDraggable, isSearch, isFilter, isCustomFilter, isCreate, isAdd, isGroup, isMerge, isSummarise, isValidationRules, isAlertRules, isRearranged, isEditable, getdata, tableid, getRearrangeData, isPushToDoc, isVisualize, isSplitViewEnabled, getFilteredData, previousFilterData, isMaster, rules, formId, handleViewMaster, isViewAllData, isMasterSwitch, indexNo, isNonBlocking, isRefresh, isDeleteRecords, onShowPageChange, currentPageNo, pageCount, onShowSizeChange, showPagination, isPrintPdf, isSummary, isOwner } = props;
    const [editingCell, setEditingCell] = useState<any>([]);
    const [editHead, setEditHead] = useState(false);
    const [rowsState, setRowsState] = useState(data);
    const [selectedRecords, SetSelectedRecords] = useState<any>("");
    const [moreOption, setmoreOption] = useState(false);
    const [searchedData, setsearchData] = useState<any>({});
    const [viewFilter, setViewFilter] = useState(false);
    const [filterColumn, setFilterColumn] = useState(null);
    const [searchedCustomData, setCustomsearchData] = useState<any>({});
    const [isViewForm, setisViewForm] = useState(isViewAllData);
    const [isAtStart, setIsAtStart] = useState(true);
    const [isAtEnd, setIsAtEnd] = useState(false);

    const handleFilterClick = (columnId) => {
        setViewFilter(!viewFilter)
        setFilterColumn(columnId.id);

    };

    useEffect(() => {

    }, [filterColumn])

    const onDragEnd = (result: any) => {
        if (!result.destination) return;

        const startIndex = result.source.index;
        const endIndex = result.destination.index;

        const updatedRows = [...rowsState];
        const [removed] = updatedRows.splice(startIndex, 1);
        updatedRows.splice(endIndex, 0, removed);

        setRowsState(updatedRows);
        getRearrangeData(updatedRows)
    };

    /**
     * state reducer to handle onclick checkbox 
     */
    const stateReducer = useCallback((newState, action, prevState) => {
        // intercept expand toggles and collapse other expanded row(s)
        if (action.type === 'toggleRowExpanded') {
            const prevTokens = Object.keys(prevState.expanded);
            const newTokens = Object.keys(newState.expanded);
            if (newTokens.length > 1) {
                const nextExpanded = {};
                for (const t of newTokens) {
                    if (!prevTokens.includes(t)) {
                        nextExpanded[t] = true;
                    }
                }
                return { ...newState, expanded: nextExpanded };
            }
        }
        isSelect && props.onAction(11, newState)
        SetSelectedRecords(newState)
        return newState;
    }, []);

    useEffect(() => {
        setRowsState(data)
    }, [data])

    useEffect(() => {
        setisViewForm(isViewAllData)
    }, [isViewAllData])

    const {
        header,
        getTableProps,
        getTableBodyProps,
        toggleAllRowsSelected,
        headerGroups,
        rows,
        prepareRow,
        allColumns,
        setGlobalFilter,
        state,
    } = useTable({ columns, data, defaultdata, stateReducer }, useFilters, useGlobalFilter, useSortBy,
        useRowSelect,
        isSelect || isSelectForm ?
            hooks => {
                hooks.visibleColumns.push(columns => [
                    // make a column for selection
                    {
                        "id": "select",
                        "label": ({ getToggleAllRowsSelectedProps }) => (
                            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} onClick={() => props.onAction(11, "all")} />
                        ),
                        accessor: elem => "-",
                        Cell: ({ value, row }) => <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />,
                        disableFilter: true,

                    },
                    ...columns,
                ])
            } : hooks => hooks.visibleColumns.push(columns => [...columns]));

    const { globalFilter } = state;
    const [showFilter, setshowFilter] = useState(false);
    const [showSearch, setshowSearch] = useState(false);
    const [filterData, setFilterData] = useState<any>(previousFilterData && previousFilterData.search_data || []);
    const [filterCustomData, setCustomFilterData] = useState<any>([]);

    let formContainerRef = useRef(null);
    useOutsideAlerter(formContainerRef);

    function useOutsideAlerter(ref) {
        useEffect(() => {
            /**
             * Alert if clicked on outside of element
             */
            function handleClickOutside(event) {
                if (ref.current && !ref.current.contains(event.target)) {
                    setshowFilter(false)
                }
            }

            // Bind the event listener
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                // Unbind the event listener on clean up
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, [ref]);
    }

    /**
     * 
     * @param rowIndex 
     * @param columnId 
     * @param value 
     */
    const handleCellEdit = (rowIndex, columnId, value) => {
        // Implement your logic for handling cell edits (e.g., updating data)
        // const updatedData = [...data];
        // updatedData[rowIndex][columnId] = value;
        // setRowsState(updatedData);
        getdata(value, rowIndex, columnId);

        // Update the data source with the new data
        // For a more robust solution, you may want to use state management tools like Redux.
        // This example assumes that `data` is part of your component state.
        // You should update the state using the appropriate method (e.g., setState).
    };



    const scrollContainerRef: any = useRef(null);

    const handleScroll = (direction) => {
        if (!scrollContainerRef.current) return;

        const scrollAmount = 100;
        const container = scrollContainerRef.current;

        if (direction === 'right' && !isAtEnd) {
            container.scrollLeft += scrollAmount;
        } else if (direction === 'left' && !isAtStart) {
            container.scrollLeft -= scrollAmount;
        }

        // Use timeout to wait for scroll to update
        setTimeout(() => {
            const maxScrollLeft = container.scrollWidth - container.clientWidth;
            setIsAtStart(container.scrollLeft <= 0);
            setIsAtEnd(container.scrollLeft >= maxScrollLeft);
        }, 100);
    };

    /**
     * 
     * @param data 
     * @param searchvalue 
     */
    const getSearchText = (data, searchvalue) => {
        let SearchArray: any = [];
        SearchArray = filterData.filter(
            item => item.field_id != data.data.id && item.search_value != ""
        );
        let searchData: any = {};

        // Check if it's a date range input
        const isDateRange =
            typeof searchvalue === "object" && searchvalue.start !== undefined;

        // TRUE only if BOTH start and end are present
        const bothDatesPresent =
            isDateRange && searchvalue.start && searchvalue.end;

        // For strings: valid only if text exists
        const hasText = typeof searchvalue === "string" && searchvalue.length > 0;

        if (bothDatesPresent || hasText) {
            // IF BLOCK → only when both dates OR valid text

            let Obj = {
                field_id: data.data.id,
                search_value: searchvalue,
                field_type: data.data.field_type,
                is_field: true,
                is_range: isDateRange,
                start_created_at: isDateRange
                    ? searchvalue.start
                        ? new Date(String(searchvalue.start))
                        : null
                    : null,
                end_created_at: isDateRange
                    ? searchvalue.end
                        ? new Date(String(searchvalue.end))
                        : null
                    : null,
                time_zone: tz
            };

            SearchArray.push(Obj);
            setFilterData(SearchArray);

            searchData = {
                search_data: SearchArray
            };

        } else if (isDateRange && (searchvalue.start || searchvalue.end)) {
            // ELSE IF BLOCK → one date present
            let Obj = {
                field_id: data.data.id,
                search_value: searchvalue.start || searchvalue.end,
                field_type: data.data.field_type,
                is_field: true,
                is_range: false,
                time_zone: tz
            }
            SearchArray.push(Obj);
            setFilterData(SearchArray);

            searchData = {
                search_data: SearchArray
            };

        } else {
            // ELSE BLOCK → no dates
            searchData = {
                search_data: SearchArray
            };
            setFilterData(SearchArray);
        }

        setsearchData(searchData);
    };



    const applyFilter = (type) => {
        if (type == "text") {
            getFilteredData(searchedData, {}, 1)
        } else {
            getFilteredData(searchedCustomData, {}, 1)
            setViewFilter(false)
        }
    }

    /**
     * get filter data multi select
     * @param value 
     * @returns 
     */
    const getMultiSearchText = (data, searchvalue) => {
        let SearchArray = filterCustomData.filter(item => item.field_id != data.data.id && item.search_value != "");
        let searchData: any = {}
        if (searchvalue.length > 0) {
            let Obj = {
                "field_id": data.data.id,
                "search_value": searchvalue,
                "field_type": data.data.field_type,
                "is_field": true

            }

            Obj["time_zone"] = tz
            SearchArray.push(Obj)
            setCustomFilterData(SearchArray)

            searchData = {
                "search_data": SearchArray
            }
        } else {
            searchData = {
                "search_data": SearchArray
            }
            setCustomFilterData(SearchArray)
        }

        setCustomsearchData(searchData)
        // if (searchData.search_data.length > 0) {
        //     if (searchvalue.length >= 3 || searchvalue.length == 0) {
        //         getFilteredData(searchData, {}, 1)
        //     }
        // } else if (searchvalue.length === 0) {
        //     getFilteredData({}, {}, 1)
        // }

    }

    const isNumber = (value) => !isNaN(value) && !isNaN(parseFloat(value));


    /**
     * handle view all form swtich
     */
    const handleViewForm = () => {
        setisViewForm(!isViewForm)
        handleViewMaster(!isViewForm)
    }
    // Render the UI for your table
    return (
        <div style={{ maxWidth: '100%' }} className="custom-table-container">
            <Row className='options-container align-items-center'>

                <Col className='table-title row m-0 align-items-center' md={props.isSummarise ? 10 : isPushToDoc ? isGroup ? 4 : 8 : 0} xs={props.isSummarise ? 5 : isPushToDoc ? 5 : 0}>
                    {
                        isEditable && editHead ?
                            <input style={{ width: "100%" }} value={props.header} onChange={(e) => getdata(e.target.value, tableid)} onBlur={() => setEditHead(false)} />
                            :
                            <span className={customClassName + '-header mr-3'} style={window.innerWidth > 700 ? {} : { fontSize: '16px' }} onClick={() => setEditHead(true)}>{props.header || 'All Entities'}  {tableid ? "" : <>| <b>{data.length}</b></>}</span>

                    }


                    {isCreate &&
                        <CustomButton type="alert-primary" onClick={() => props.onAction(4)} className="w-100" text="Create"></CustomButton>
                    }
                    {
                        props.isRearranged &&
                        <CustomButton type="alert-primary" onClick={() => props.onAction(15)} className="w-100" text="Save Rearranged"></CustomButton>
                    }
                    {props.isSummarise &&
                        <>
                            <CustomButton type="alert-primary" onClick={() => props.onAction(8)} className="w-100" text="View Combined Data"></CustomButton>
                        </>
                    }
                    {
                        props.isSelectForm &&
                        <CustomButton type="alert-primary" onClick={() => props.onAction(19, selectedRecords)} className="w-100" text="Create Cumulative"></CustomButton>

                    }
                    {props.isValidationRules &&
                        <>
                            <CustomButton type="alert-primary" onClick={() => props.onAction("addValidationRules")} className="w-100" text="Add Validation Rules"></CustomButton>
                        </>
                    }
                    {props.isAlertRules &&
                        <>
                            <CustomButton type="alert-primary" onClick={() => props.onAction("addAlertRules")} className="w-100" text="Add Alert Rules"></CustomButton>
                        </>
                    }
                    {props.isProjectLevelOcr &&
                        <>
                            <CustomButton type="alert-primary" onClick={() => props.onAction("projectLevelOcr")} className="w-100" text="Project Level OCR"></CustomButton>
                        </>
                    }
                    {props.printableTemplate &&
                        <>
                            <CustomButton type="alert-primary" onClick={() => props.onAction("printableTemplate")} className="w-100" text="Printable Template"></CustomButton>
                        </>
                    }
                    {
                        props.isEditRecords &&
                        <CustomButton type="alert-primary" onClick={() => props.onAction("editRecords")} className='data-btn no-print' text="Edit Records"></CustomButton>
                    }
                    {/* 
                    {
                        isMerge &&
                        <CustomButton type="alert-primary" onClick={() => props.onAction(12)} className='data-btn no-print' text="Merge"></CustomButton>

                    } */}

                </Col>

                {window.innerWidth > 700 &&
                    <>
                        <Col md={isPushToDoc ? 2 : 0} >
                            {
                                isPushToDoc &&
                                <CustomButton type="alert-primary" onClick={() => props.onAction(17)} className='data-btn no-print' text="Copy to Editor"></CustomButton>
                            }
                        </Col>
                        <Col md={2}>
                            {
                                isVisualize &&
                                <CustomButton type="alert-primary" onClick={() => props.onAction(18)} className='data-btn no-print' text="Visualize"></CustomButton>
                            }
                        </Col>
                        {/* {isSplitViewEnabled &&
                            <Col md={2}>
                                <CustomButton type="alert-primary" onClick={() => props.onAction("splitView")} className='data-btn no-print' text="Split View"></CustomButton>
                            </Col>
                        } */}
                    </>
                }


                {isGroup &&
                    <>
                        <Col md={2}>
                            <CustomButton type="alert-primary" onClick={() => props.onAction(6)} text="Configure Grouping"></CustomButton>
                        </Col>
                        <Col md={2}>
                            <CustomButton type="alert-primary" onClick={() => props.onAction(9)} className='data-btn no-print' text="Configure Analysis"></CustomButton>
                        </Col>
                    </>
                }


                {isAdd &&
                    <Col className='text-right'>
                        <div className='d-flex m-0 justify-content-end'>
                            {isAdd &&
                                <CustomButton type="alert-primary" onClick={() => props.onAction(5)} className="w-50" text="Add"></CustomButton>
                            }
                        </div>
                    </Col>
                }



                <Col className='text-right' md={12} xs={12}>
                    <div className='d-flex mt-2 justify-content-end'>
                        {
                            isCustomFilter &&
                            <div className='search-container'>
                                <Button className='table-header-button' size="sm" onClick={() => props.onAction("question")}>
                                    <i className="icon icon-question"></i>
                                </Button>
                                <Button className='table-header-button' style={{ margin: "0px 3px" }} size="sm" onClick={() => props.onAction(13)}>
                                    <i className="icon icon-filter"></i>
                                </Button>
                                <Button className='table-header-button' style={{ margin: "0px 3px" }} size="sm" onClick={() => props.onAction(14)}>
                                    <ClearOutlined />
                                </Button>
                                {isPrintPdf &&
                                    <Button
                                        className='table-print-button'
                                        size="sm"
                                        onClick={() => props.onAction("printPdf")}
                                        title="Print / Export to PDF"
                                    >
                                        <PrinterOutlined />
                                    </Button>
                                }
                                {
                                    window.innerWidth < 750 &&
                                    <i className={moreOption ? "icon icon-close" : " ml-2 icon icon-more"} onClick={() => setmoreOption(!moreOption)}></i>

                                }

                                {
                                    window.innerWidth < 750 &&
                                    <>
                                        <div className={moreOption ? "more-options-open" : "more-options"} >
                                            {
                                                moreOption ?
                                                    <div className="more-container" onClick={() => setmoreOption(false)}>
                                                        <div className="form-actions">
                                                            {
                                                                isPushToDoc &&
                                                                <div className='copy' onClick={() => props.onAction(17)} >
                                                                    Copy to Editor
                                                                </div>
                                                            }

                                                            {
                                                                isVisualize &&
                                                                <div className="visualise" onClick={() => props.onAction(18)}  >
                                                                    Visualize
                                                                </div>

                                                            }
                                                        </div>

                                                    </div> : ""
                                            }


                                        </div>


                                    </>
                                }


                            </div>
                        }

                        {isMasterSwitch ?
                            <div className='d-flex align-items-center'>
                                <Switch
                                    checked={isViewForm}
                                    onChange={() => handleViewForm()}
                                    onColor={getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() || "#007aff"}
                                    offColor="#7c7b7b"
                                    onHandleColor="#fff"
                                    offHandleColor="#fff"
                                    handleDiameter={20}
                                    width={100}
                                    uncheckedIcon={
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: 'flex-start',
                                                textWrap: 'nowrap',
                                                height: "100%",
                                                fontSize: 12,
                                                color: "white",
                                                paddingLeft: 0,
                                            }}
                                        >
                                            All
                                        </div>
                                    }
                                    checkedIcon={
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                textWrap: 'nowrap',
                                                height: "100%",
                                                fontSize: 12,
                                                color: "white",
                                                paddingLeft: 8,
                                            }}
                                        >
                                            View Master
                                        </div>
                                    }
                                />
                                {isOwner &&
                                    <Button
                                        className='table-header-button ml-2'
                                        size="sm"
                                        style={{
                                            fontSize: '12px',
                                            height: '26px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '0 8px',
                                        }}
                                        onClick={() => props.onAction("updateViewMaster", isViewForm)}
                                    >
                                        Save
                                    </Button>
                                }
                            </div>
                            : ""
                        }
                        {isSearch &&
                            <div className='search-container'>
                                {showSearch ?
                                    <InputGroup>
                                        <Input type="search" className="data-Table-common_search"
                                            placeholder={searchHelpText} bsSize="sm"
                                            onChange={(e) => setGlobalFilter(e.target.value)}
                                            value={globalFilter}
                                        />
                                        <Button addonType="append">
                                            <i className="icon icon-close search-button" onClick={() => { setGlobalFilter(""); setshowSearch(false) }}></i>
                                        </Button>
                                    </InputGroup> :
                                    <Button className='table-header-button' size="sm" onClick={() => setshowSearch(true)}>
                                        <i className="icon icon-search"></i>
                                    </Button>
                                }
                            </div>
                        }
                        {isFilter &&
                            <Button className='table-header-button' size="sm" onClick={() => setshowFilter(true)}>
                                <i className="icon icon-filter"></i>
                            </Button>
                        }
                        {isRefresh &&
                            <Button className='table-refresh-button' size="sm" onClick={() => props.onAction("refreshPage")}>
                                <ReloadOutlined />
                            </Button>
                        }
                        {isSplitViewEnabled &&
                            <Button className='table-header-button ml-1' size="sm" title="Split View" onClick={() => props.onAction("splitView")}>
                                <LayoutOutlined />
                            </Button>
                        }
                        {isDeleteRecords &&
                            <Button className='table-records-delete-button' size="sm" onClick={() => props.onAction("deleteRecords")}>
                                <i className="icon icon-delete"></i>
                            </Button>
                        }

                    </div>
                </Col>

                {showFilter &&
                    <div>
                        <div ref={formContainerRef} className="filter-body">
                            {allColumns.map(column => (
                                <div key={column.id}>
                                    <label>
                                        <input type="checkbox" {...column.getToggleHiddenProps()} />{' '}
                                        {column.label}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                }

            </Row>
            {
                isAtStart ?
                    ""
                    :
                    <div
                        style={{
                            position: 'absolute',
                            left: '0%',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            borderLeft: '1px solid #ccc',
                            backgroundColor: 'var(--primary-color, #006BD9)',
                            cursor: 'pointer',
                            padding: '5px 5px',
                            borderRadius: '5px'
                        }}
                        onClick={() => handleScroll('left')}

                    >
                        <LeftOutlined style={{ color: '#fff' }} />
                    </div>
            }

            {/* style={{ overflow: 'auto', position: 'relative' }} */}
            <div ref={scrollContainerRef} className='main-table'>
                <DragDropContext onDragEnd={onDragEnd}>
                    <table {...getTableProps()} className={customClassName + " custom-table"}>
                        <thead className='table-header sticky-header'>
                            {
                                headerGroups.map((headerGroup: any, headerIndex) => (
                                    <tr key={headerIndex}  {...headerGroup.getHeaderGroupProps()} className="table-row">
                                        {/* <th className='text-center sticky-col-header'><Checkbox/></th> */}
                                        {headerGroup.headers.map((column: any, index) => (

                                            <th
                                                key={column.id}
                                                style={{ minWidth: column.width + 'px' }}
                                                className={`table-head sticky-th ${column?.className || ''}`}
                                                {...column.getHeaderProps()}
                                            >
                                                {/* {column.render('label')} */}

                                                {
                                                    isCustomFilter && !column.disableFilter &&
                                                    <div style={{ display: 'flex', justifyContent: 'space-around', columnGap: '5px' }}>{column.canFilter && indexNo == index || indexNo == undefined ? <ColumnFilter getSearchText={getSearchText} applyFilter={applyFilter} column={column} previousData={filterData} /> : null}
                                                        {formId && indexNo == index || indexNo == undefined && <FilterFilled onClick={() => handleFilterClick(column && column)} />}
                                                    </div>
                                                }
                                                {/* {
                                                        formId &&
                                                        <div>
                                                            <FilterFilled onClick={() => handleFilterClick(column && column)} />
                                                        </div>
                                                    } */}
                                                {viewFilter && filterColumn === column.id &&
                                                    <div>{column.canFilter ? <ColumnFilterUnique formId={formId} onCancel={() => setViewFilter(false)} getSearchText={getMultiSearchText} applyFilter={applyFilter} column={column} previousData={filterCustomData} /> : null}</div>
                                                }

                                                {
                                                    isSort ?
                                                        <div style={{ display: 'flex' }}>
                                                            <div title={column.render('label')} className='cell-data-txt'>{column.render('label')}</div>
                                                            <div {...column.getHeaderProps(column.getSortByToggleProps())}>
                                                                {(typeof column.render('label') == "string") && isSort ? column.isSorted ? (!column.isSortedDesc ?
                                                                    <Sort coloractive={'#fff'} colorinactive={'#ccc'} /> :
                                                                    <Sort coloractive={'#ccc'} colorinactive={'#fff'} />
                                                                ) : <Sort coloractive={'#fff'} colorinactive={'#fff'} /> : ''}
                                                            </div>
                                                        </div>
                                                        :
                                                        <div title={column.render('label')} className='cell-data-txt'>{column.render('label')}</div>

                                                }

                                            </th>



                                        ))}
                                        {
                                            props.isSurvey ? <th className='table-head text-center' style={{ whiteSpace: "nowrap" }}>Child Survey Link</th> : ""
                                        }
                                        {(props.isView || props.isEdit || props.isDelete) && <th className='table-head text-center sticky-col-header'>Actions</th>}
                                    </tr>
                                ))}
                        </thead>
                        {isDraggable ?
                            <Droppable droppableId="table">
                                {(provided) => (
                                    <tbody {...getTableBodyProps()} ref={provided.innerRef} className="table-body">
                                        {rows.length > 0 ?
                                            rows.map((row: any, i: any) => {
                                                prepareRow(row)
                                                return (
                                                    <Draggable key={row.id} draggableId={row.id} index={i}>
                                                        {
                                                            isDraggable ?
                                                                (provided, snapshot) => (
                                                                    <tr {...row.getRowProps()}
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                        style={{
                                                                            ...provided.draggableProps.style,
                                                                            background: snapshot.isDragging ? "darkgrey" : "inherit",
                                                                        }}
                                                                        className="table-row"
                                                                    >
                                                                        {row.cells.map((cell: any) => (
                                                                            <td
                                                                                style={{ minWidth: cell.column.width + 'px' }}
                                                                                {...cell.getCellProps()}
                                                                                className={'table-data ' + (cell.column?.className || '')}
                                                                            >
                                                                                {/* Don't make the select checkbox cell editable! */}
                                                                                {cell.column.id === 'select' ? (
                                                                                    cell.render('Cell')
                                                                                ) : isEditable ? (
                                                                                    editingCell && editingCell.i === i && editingCell.columnId === cell.column.id ? (
                                                                                        <input
                                                                                            type="text"
                                                                                            value={cell.value}
                                                                                            onChange={e => handleCellEdit(i, cell.column.id, e.target.value)}
                                                                                            onBlur={() => setEditingCell([])}
                                                                                        />
                                                                                    ) : (
                                                                                        <div
                                                                                            style={{ cursor: 'pointer' }}
                                                                                            onClick={() => setEditingCell({ i, columnId: cell.column.id })}
                                                                                        >
                                                                                            {cell.render('Cell')}
                                                                                        </div>
                                                                                    )
                                                                                ) : (
                                                                                    cell.render('Cell')
                                                                                )}
                                                                            </td>
                                                                        ))}

                                                                        {(props.isView || props.isEdit || props.isDelete) &&
                                                                            <td className="table-data text-center sticky-col">
                                                                                {props.isMaster && props.isSummary && (
                                                                                    <Tooltip title="View AI Summary" placement="top">
                                                                                        <FileTextOutlined
                                                                                            className="table-file-icon"
                                                                                            onClick={() => props.onAction("onViewSummary", row, i)}
                                                                                        />
                                                                                    </Tooltip>
                                                                                )}

                                                                                {props.isView && (
                                                                                    <i
                                                                                        className="icon icon-eye"
                                                                                        onClick={() => props.onAction(1, row, i)}
                                                                                    />
                                                                                )}

                                                                                {props.isEdit && (
                                                                                    <i
                                                                                        className="icon icon-edit"
                                                                                        onClick={() => props.onAction(2, row, i)}
                                                                                    />
                                                                                )}

                                                                                {props.isDelete && (
                                                                                    <i
                                                                                        className="icon icon-delete"
                                                                                        onClick={() => props.onAction(3, row, i)}
                                                                                    />
                                                                                )}
                                                                            </td>
                                                                        }
                                                                    </tr>
                                                                )
                                                                :
                                                                <tr {...row.getRowProps()}>
                                                                    {row.cells.map((cell: any) => {
                                                                        return (
                                                                            <td
                                                                                style={{ minWidth: cell.column.width + 'px' }}
                                                                                {...cell.getCellProps()}
                                                                                className={'table-data ' + (cell.column?.className || '')}
                                                                            >
                                                                                {/* Only enable editing for non-select columns */}
                                                                                {cell.column.id === 'select' ? (
                                                                                    cell.render('Cell')
                                                                                ) : isEditable ? (
                                                                                    editingCell && editingCell.i === i && editingCell.columnId === cell.column.id ? (
                                                                                        <input
                                                                                            type="text"
                                                                                            value={cell.value}
                                                                                            onChange={e => handleCellEdit(i, cell.column.id, e.target.value)}
                                                                                            onBlur={() => setEditingCell([])}
                                                                                        />
                                                                                    ) : (
                                                                                        <div
                                                                                            style={{ cursor: 'pointer' }}
                                                                                            onClick={() => setEditingCell({ i, columnId: cell.column.id })}
                                                                                        >
                                                                                            {cell.render('Cell')}
                                                                                        </div>
                                                                                    )
                                                                                ) : (
                                                                                    cell.render('Cell')
                                                                                )}
                                                                            </td>
                                                                        );
                                                                    })}

                                                                    {(props.isView || props.isEdit || props.isDelete) &&
                                                                        <td className="table-data text-center sticky-col">
                                                                            {props.isMaster && props.isSummary && (
                                                                                <Tooltip title="View AI Summary" placement="top">
                                                                                    <FileTextOutlined
                                                                                        className="table-file-icon"
                                                                                        onClick={() => props.onAction("onViewSummary", row, i)}
                                                                                    />
                                                                                </Tooltip>
                                                                            )}

                                                                            {props.isView && (
                                                                                <i
                                                                                    className="icon icon-eye"
                                                                                    onClick={() => props.onAction(1, row, i)}
                                                                                />
                                                                            )}

                                                                            {props.isEdit && (
                                                                                <i
                                                                                    className="icon icon-edit"
                                                                                    onClick={() => props.onAction(2, row, i)}
                                                                                />
                                                                            )}

                                                                            {props.isDelete && (
                                                                                <i
                                                                                    className="icon icon-delete"
                                                                                    onClick={() => props.onAction(3, row, i)}
                                                                                />
                                                                            )}
                                                                        </td>
                                                                    }
                                                                </tr>
                                                        }
                                                    </Draggable>
                                                );
                                            })
                                            :
                                            (
                                                <tr>
                                                    <td colSpan={columns.length} style={{ textAlign: "center", padding: "10px", color: "gray" }}>
                                                        No Data Available
                                                    </td>
                                                </tr>
                                            )}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>
                            :
                            <tbody {...getTableBodyProps()} className="table-body">
                                {rows.length > 0 ? rows.map((row: any, i: any) => {
                                    prepareRow(row)
                                    const rule = rules && rules.filter(itm => itm.includes('alertrule'))
                                    let colKeys = Object.keys(row.values)
                                    let bgColor = { backgroundColor: '#FFFFFF' };
                                    let isBlock = false;
                                    let alertArray: any = [];
                                    /**
                                     * check for alert rules
                                     * and apply color code depending on combinations
                                     */
                                    if (rule && rule.length > 0) {
                                        rule.map(itms => {
                                            let part1 = itms.split('endrule')[0];
                                            let expression = part1.split(':')[2];

                                            let evalData = expression.trim().split(' ').map(val => {
                                                let valExist = colKeys && colKeys.includes(val);
                                                let configOpt = ComputeOperatorsData.filter(itm => itm.value == val)
                                                if (valExist) {
                                                    return isNumber(row.values[val]) ? row.values[val] : `"${row.values[val]}"`;
                                                } else if (configOpt.length > 0) {
                                                    return val;  // Keep operators as they are
                                                } else {
                                                    return `"${val.replaceAll("'", "")}"`;
                                                }
                                            }).join(' '); // formatting without extra spaces


                                            try {
                                                let result = eval(evalData);
                                                if (result) {
                                                    let color = part1.split(':')[3].trim();
                                                    alertArray.push(colorCodes.filter(itm => itm.value == color)[0])
                                                } else {
                                                    alertArray.push(colorCodes.filter(itm => itm.value == 'white')[0])
                                                }
                                            } catch (error) {
                                                console.error("Eval Error:", error);
                                            }
                                        });
                                        const sortedColors = [...alertArray].sort((a, b) => a.priority - b.priority);

                                        //if atleast one condition failed i.e, false than make it red
                                        if (alertArray.length > 0) {

                                            isBlock = sortedColors[0]?.value.includes('block')
                                            if (isNonBlocking || props.isMaster && isBlock) {
                                                row.checkMandatory = true;
                                            }
                                            //}
                                            bgColor = { backgroundColor: sortedColors && sortedColors[0]?.code }
                                        } else {
                                            bgColor = { backgroundColor: '#FFFFFF' }
                                        }
                                    }

                                    /*
                                        New Alert rules coming from backend
                                    */
                                    const priorityMap: Record<string, number> = {
                                        red: 1,
                                        yellow: 2,
                                        green: 3,
                                    };
                                    const colorHexMap: Record<string, string> = {
                                        red: "#ff6262",
                                        yellow: "#FFDE21",
                                        green: "#16a34a",
                                    };


                                    let alerts = row?.original?.alertRules || [];
                                    if (alerts.length > 0) {
                                        let selectedColor = "white"; // default color

                                        let highestPriority = Infinity;

                                        for (const alert of alerts) {
                                            const color = alert.color;
                                            const priority = priorityMap[color];
                                            if (priority !== undefined && priority < highestPriority) {
                                                highestPriority = priority;
                                                selectedColor = color;
                                            }
                                        }

                                        const rowColor = colorHexMap[selectedColor] || "#ffffff"; // fallback
                                        bgColor = { backgroundColor: rowColor };

                                    }

                                    return (
                                        <tr {...row.getRowProps()} className='table-row' style={bgColor}>
                                            {row.cells.map((cell: any) => {
                                                // Improved conditional: keep checkbox cell simple, handle editability & special date fields
                                                const fieldType = cell.column?.data?.field_type;
                                                const isDateOnly = fieldType === "dateField";
                                                const isDateTime = fieldType === "dateTime";


                                                return (
                                                    <td
                                                        style={{ minWidth: cell.column.width + 'px' }}
                                                        // Only apply onClick for non-checkbox ("select") columns
                                                        onClick={
                                                            cell.column.id !== 'select'
                                                                ? () =>
                                                                    props.isMaster || (isNonBlocking && !isBlock)
                                                                        ? props.onAction('master', row, i)
                                                                        : ""
                                                                : undefined
                                                        }
                                                        {...cell.getCellProps()}
                                                        className={'table-data ' + (cell.column?.className || '')}
                                                    >
                                                        {cell.column.id === 'select' ? (
                                                            cell.render('Cell')
                                                        ) : isEditable ? (
                                                            editingCell && editingCell.i === i && editingCell.columnId === cell.column.id ? (
                                                                <input
                                                                    type="text"
                                                                    value={cell.value}
                                                                    onChange={e => handleCellEdit(i, cell.column.id, e.target.value)}
                                                                    onBlur={() => setEditingCell([])}
                                                                />
                                                            ) : (
                                                                <div
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={() => setEditingCell({ i, columnId: cell.column.id })}
                                                                >
                                                                    {isDateOnly
                                                                        ? moment(cell.value).format("YYYY-MM-DD")
                                                                        : isDateTime
                                                                            ? convertToIST(cell.value)
                                                                            : cell.render('Cell')}
                                                                </div>
                                                            )
                                                        ) : (
                                                            isDateOnly
                                                                ? moment(cell.value).format("YYYY-MM-DD")
                                                                : isDateTime
                                                                    ? convertToIST(cell.value)
                                                                    : cell.render('Cell')
                                                        )}

                                                    </td>
                                                );
                                            })}

                                            {
                                                props.isSurvey &&
                                                <td className='table-data text-center'>
                                                    <i className="icon icon-copy" onClick={() => props.onAction(16, row, i)}></i>
                                                </td>
                                            }
                                            {(props.isView || props.isEdit || props.isDelete) &&
                                                <td className="table-data text-center sticky-col">
                                                    <div className="table-actions">
                                                        {props.isMaster && props.isSummary && (
                                                            <Tooltip title="View AI Summary" placement="top">
                                                                <FileTextOutlined
                                                                    className="table-action-icon"
                                                                    onClick={() => props.onAction("onViewSummary", row, i)}
                                                                />
                                                            </Tooltip>
                                                        )}

                                                        {props.isView && (
                                                            <i
                                                                className="icon icon-eye table-action-icon"
                                                                onClick={() => props.onAction(1, row, i)}
                                                            ></i>
                                                        )}

                                                        {props.isEdit && (
                                                            <i
                                                                className="icon icon-edit table-action-icon"
                                                                onClick={() => props.onAction(2, row, i)}
                                                            ></i>
                                                        )}

                                                        {props.isDelete && (
                                                            <i
                                                                className="icon icon-delete table-action-icon"
                                                                onClick={() => props.onAction(3, row, i)}
                                                            ></i>
                                                        )}
                                                    </div>
                                                </td>

                                            }
                                        </tr>
                                    )

                                }) :
                                    (
                                        <tr>
                                            <td colSpan={columns.length} style={{ textAlign: "center", padding: "10px", color: "gray" }}>
                                                No Data Available
                                            </td>
                                        </tr>
                                    )}
                            </tbody>
                        }

                    </table>
                </DragDropContext>
            </div>
            {
                isAtEnd ?
                    ""
                    :
                    <div
                        style={{
                            position: 'absolute',
                            right: '5px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 1,
                            borderLeft: '1px solid #ccc',
                            backgroundColor: 'var(--primary-color, #006BD9)',
                            cursor: 'pointer',
                            padding: '5px 5px',
                            borderRadius: '5px'
                        }}
                        onClick={() => handleScroll('right')}

                    >
                        <RightOutlined style={{ color: '#fff' }} />
                    </div>
            }
            {showPagination && <div className='viewdata-pagination'>
                <Pagination
                    current={currentPageNo}
                    total={pageCount}
                    onChange={onShowPageChange}
                    onShowSizeChange={(page, size) =>
                        onShowSizeChange(page, size, searchedData)
                    }
                    showSizeChanger={true}
                />
            </div>}

        </div>
    )
}

function CustomTable(props: any) {

    const columns = React.useMemo(
        () => props.tableProps,
        [props.tableProps]
    )


    return (
        <Table
            {...props}
            columns={columns}
            data={props.data}
            pagination={true}
        />
    )
}

export default CustomTable
